import React, { useCallback, useEffect, useState } from "react";
import { Truck, PackageCheck, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { OrderStatusPill } from "@/components/supply/shared";

export default function FieldDeliveries() {
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/refill-orders", { params: { mine: true } }).then(({ data }) => setOrders(data.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (o, action) => {
    setBusy(o.id);
    try { await api.post(`/refill-orders/${o.id}/${action}`); toast.success(action === "pickup" ? "Picked up — head to the household" : "Delivered — return the empty to the station"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="field-deliveries">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-flame-400 font-semibold">Deliveries</div>
        <h1 className="font-heading text-xl font-bold">Refill runs</h1>
        <p className="text-xs text-slate-400 mt-1">Collect from the station, deliver, then bring the empty back to the same station.</p>
      </div>
      {orders.length === 0 && <div className="text-sm text-slate-500 border border-dashed border-slate-700 rounded-lg p-6 text-center">No deliveries right now.</div>}
      {orders.map((o, i) => (
        <div key={o.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid={`delivery-card-${i}`}>
          <div className="flex items-center justify-between"><span className="font-mono text-xs text-slate-300">{o.number}</span><OrderStatusPill status={o.status} /></div>
          <div>
            <div className="font-semibold text-slate-100">{o.household_name} · {o.cylinder_type}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin size={11} />{o.household_address || "No address"}</div>
            {o.household_phone && <a href={`tel:+${o.household_phone}`} className="text-xs text-flame-400 flex items-center gap-1 mt-1"><Phone size={11} />+{o.household_phone}</a>}
            <div className="text-xs text-slate-400 mt-1">Station: <span className="text-slate-200">{o.station_name}</span></div>
          </div>
          {o.status === "RELEASED" && (
            <button disabled={busy === o.id} onClick={() => act(o, "pickup")} data-testid={`delivery-pickup-${i}`}
              className="w-full bg-flame-600 hover:bg-flame-500 text-white rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"><Truck size={16} />Picked up at station</button>
          )}
          {o.status === "PICKED_UP" && (
            <button disabled={busy === o.id} onClick={() => act(o, "deliver")} data-testid={`delivery-deliver-${i}`}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"><PackageCheck size={16} />Delivered to household</button>
          )}
          {o.status === "DELIVERED" && <div className="text-xs text-amber-300">Return the empty to {o.station_name} — the station confirms it.</div>}
        </div>
      ))}
    </div>
  );
}
